import React, {useCallback, useMemo} from 'react';
import {TypeContext} from './TypeContext';
import {scaleFactors} from '../utils/scale-factors';
import {css} from '@emotion/react';
import {i18n} from '~/i18n';

// Be sure to also search and replace any max-width: 65ch in .scss files if you change this. Scss cannot import js variables.
export const maxWidthCh = 45;
export const maxWidthChs = [45, 55, 65];
export const sceneWidthOptions = [
  {
    text: i18n('scene-width.small'),
    value: 0,
  },
  {
    text: i18n('scene-width.medium'),
    value: 1,
  },
  {
    text: i18n('scene-width.wide'),
    value: 2,
  },
  {
    text: i18n('scene-width.full'),
    value: 3,
  },
];

const baseWidth = 800;
const minFontSize = 16;
const maxFontSize = 20;
const baseLineHeight = 1.2;
const scaleFactor = scaleFactors.PERFECT_FOURTH;

export const Typescale = ({width, debug = false, children}) => {
  const baseFontSize = useMemo(
    () =>
      Math.min(Math.max(Math.floor(width / baseWidth * minFontSize), minFontSize), maxFontSize),
    [width],
  );

  const getTypeStyle = useCallback(
    (
      scale,
      {
        multiplier = 1,
        marginTop: marginTopCount = 0,
        marginBottom: marginBottomCount = 1,
        lineHeight: lineHeightOverride = baseLineHeight,
      } = {},
    ) => {
      const fontSize = baseFontSize * Math.pow(scaleFactor, scale) * multiplier;
      const gridHeight = baseFontSize * lineHeightOverride;

      const lineHeight = fontSize * lineHeightOverride;
      const marginTop = marginTopCount * gridHeight;
      const marginBottom
        = gridHeight - (lineHeight % gridHeight || gridHeight) + marginBottomCount * gridHeight;

      return {
        style: {
          fontSize: `${fontSize}px`,
          lineHeight: `${lineHeight}px`,
          marginTop: `${marginTop}px`,
          marginBottom: `${marginBottom}px`,
        },
        metrics: {
          fontSize,
          lineHeight,
          marginTop,
          marginBottom,
        },
      };
    },
    [baseFontSize],
  );

  const style = useMemo(() => {
    const {style: paragraphStyle} = getTypeStyle(0);
    const {style: paragraphStyleNoMargin} = getTypeStyle(0, {marginBottom: 0});
    const paragraphCssNoMargin = css(paragraphStyleNoMargin);
    const paragraphCss = css(paragraphStyle);

    return css`
      ${paragraphCssNoMargin};

      p {
        ${paragraphCss};
      }
      h6 {
        ${paragraphCssNoMargin};
        font-weight: bold;
      }
      h4,
      h5 {
        ${css(getTypeStyle(1, {marginBottom: 0}).style)};
        font-weight: bold;
      }
      h3 {
        ${css(getTypeStyle(2, {marginBottom: 0}).style)};
        font-weight: bold;
      }
      h2 {
        ${css(getTypeStyle(3, {marginBottom: 0}).style)};
        font-weight: bold;
      }
      h1 {
        ${css(getTypeStyle(3, {
      marginTop: 1,
      marginBottom: 0,
    }).style)};
        font-weight: bold;
      }
    `;
  }, [getTypeStyle]);

  return (
    <TypeContext.Provider
      value={{
        baseFontSize,
        lineHeight: baseLineHeight,
        scaleFactor,
        getTypeStyle,
        style,
        debug,
      }}
    >
      {children}
    </TypeContext.Provider>
  );
};
